import { Alert, Button, Input, Modal, Table } from 'antd';
import React, { useState } from 'react';
import { apiErrorText } from '../../../helpers/NotificationHelper';
import { BulkValidatorGameResult, MatchesClient } from '../../../utils/api';

const statusLabels = { created: 'Created', inviteResent: 'Invite resent', refused: 'Refused' };

export default function BulkCreateValidatorGames({ matchId }: { matchId: number }): React.ReactElement {
    const [visible, setVisible] = useState(false);
    const [emails, setEmails] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string>();
    const [results, setResults] = useState<BulkValidatorGameResult[]>();
    const hasEmails = emails.split(/[,\s]+/).some(Boolean);

    const create = async (): Promise<void> => {
        if (saving || !hasEmails) return;
        setSaving(true);
        setError(undefined);
        try {
            setResults(await new MatchesClient().bulkCreateValidatorGames(matchId, { emails }));
        } catch (failure) {
            setError(apiErrorText(failure));
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <Button onClick={(): void => {
                setEmails('');
                setError(undefined);
                setResults(undefined);
                setVisible(true);
            }}>Bulk create validator games</Button>
            <Modal title="Bulk create validator games" visible={visible} width={800}
                okText="Create games" onOk={(): void => { void create(); }}
                onCancel={(): void => { if (!saving) setVisible(false); }}
                okButtonProps={{ disabled: !hasEmails, loading: saving }}
                cancelButtonProps={{ disabled: saving }}
                closable={!saving} maskClosable={!saving} keyboard={!saving}
                footer={results ? <Button onClick={(): void => setVisible(false)}>Close</Button> : undefined}
                destroyOnClose>
                {results ? (
                    <>
                        <p aria-live="polite">
                            Created: {results.filter(row => row.status === 'created').length}{' · '}
                            Invite resent: {results.filter(row => row.status === 'inviteResent').length}{' · '}
                            Refused: {results.filter(row => row.status === 'refused').length}
                        </p>
                        <Table<BulkValidatorGameResult> dataSource={results} rowKey="email"
                            pagination={false} size="small" scroll={{ y: 360 }}
                            columns={[
                                { title: 'Email', dataIndex: 'email', render: (email: string) => <span style={{ overflowWrap: 'anywhere' }}>{email}</span> },
                                { title: 'Result', dataIndex: 'status', render: (status: BulkValidatorGameResult['status']) => statusLabels[status] },
                                { title: 'Details', render: (_: unknown, row: BulkValidatorGameResult) => row.error || (
                                    <a href={`/match-log/${row.matchId}`}>Open validator game</a>
                                ) },
                            ]} />
                    </>
                ) : (
                    <>
                        <p>Use match {matchId} as the template. Paste up to 300 unique email addresses,
                            separated by commas, spaces or newlines. Duplicate addresses are ignored.</p>
                        <label htmlFor={`bulk-validator-emails-${matchId}`}>Scorer emails</label>
                        <Input.TextArea id={`bulk-validator-emails-${matchId}`} value={emails} rows={8}
                            disabled={saving} autoComplete="off"
                            onChange={(event): void => setEmails(event.target.value)} />
                        {saving && <p role="status">Processing emails in order. This may take a few minutes.</p>}
                        {error && <Alert type="error" showIcon message={error} style={{ marginTop: 12 }} />}
                    </>
                )}
            </Modal>
        </>
    );
}
